import { Component, computed, inject, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@cmi/addin-core';
import { skipWhile } from 'rxjs';
import { MailService } from './mail-upload/mail.service';
import { SelectionService } from './selection/selection.service';

/**
 * The shell around the filing pages: names the open mail, and hosts either the
 * filing steps (`/file`), where the mail went (`/filed`), or the mails
 * selected together (`/batch`).
 */
@Component({
  selector: 'cmi-home',
  imports: [TranslatePipe, RouterOutlet],
  templateUrl: './home.html',
  // Fills the scrolling area even when there is little to show, so the action
  // bar at the end of the flow has a bottom to sit on.
  host: { class: 'flex min-h-full flex-col' }
})
export class Home {
  private readonly _mailService = inject(MailService);
  private readonly _router = inject(Router);

  /** Which mail is about to be filed — no longer obvious once the pane is pinned. */
  protected readonly openMail = this._mailService.openMail;
  private readonly _selectionService = inject(SelectionService);

  /** Several mails selected, or a batch held on its own: the batch names them itself. */
  protected readonly multiple = computed(
    () => this._selectionService.multiple() || this._selectionService.batchHeld()
  );

  public constructor() {
    // A pinned pane outlives the mail it was opened for. Each new mail goes
    // through the entry point again, which picks its page, and so does a
    // change between one mail and several. Changes within a selection of
    // several stay with the batch, as does any change while a batch holds on
    // to its mails. The router has done that already for what the pane opened
    // on, taken here rather than from the first value: that one comes from an
    // effect, after a change in between would have slipped by.
    const paneIsAbout = computed(() => (this.multiple() ? 'batch' : this.openMail()));
    const openedOn = untracked(paneIsAbout);

    toObservable(paneIsAbout)
      .pipe(
        skipWhile((about) => about === openedOn),
        takeUntilDestroyed()
      )
      .subscribe(() => void this._router.navigateByUrl('/'));
  }
}
